/**
 * Подписи ключей доступа: команда подключения, срок, последнее обращение и
 * то, чем ключ сужен.
 *
 * Даты — через `Intl`, как везде в трекере (см. features/issues/format): свои
 * локали dayjs пришлось бы подключать ради одной колонки.
 *
 * «4 дня назад» вместо метки времени не для красоты: колонка отвечает на
 * вопрос «этим ключом ещё пользуются?», и на него отвечает давность, а не
 * точная дата.
 */
import { intlLocale } from '@/features/issues/format'

import type { ApiKey } from './api'

/**
 * Подмножество `t` из vue-i18n: строка, подстановки и плюрализация числом.
 * Чужой полный тип сюда тащить незачем — форматтерам хватает этого.
 */
type Translate = {
  (key: string): string
  (key: string, named: Record<string, unknown>): string
  (key: string, plural: number): string
}

/** Сколько дней назад показываем относительной фразой, а не датой. */
const RELATIVE_DAYS = 30

/**
 * Команда подключения агента.
 *
 * Ключ в ней — плейсхолдер: настоящий показывается один раз, в диалоге
 * выпуска, и подставляется человеком. Живой ключ сюда подставлять нельзя —
 * экран открывают при других людях и на общих созвонах.
 */
export const connectCommand = (origin = window.location.origin): string =>
  `claude mcp add --transport http vp-tracker ${origin}/mcp \\\n  --header "Authorization: Bearer vp_live_…"`

/** Чем ключ сужен, человеческими словами. */
export const scopesLabel = (scopes: string[] | undefined, t: Translate): string => {
  if (!scopes || scopes.length === 0) return t('settings.integrations.keys.scopes.all')

  const parts: string[] = []

  if (scopes.some((scope) => scope.startsWith('tracker:'))) {
    parts.push(
      scopes.includes('tracker:write')
        ? t('settings.integrations.keys.scopes.tasks')
        : t('settings.integrations.keys.scopes.tasksRead'),
    )
  }

  if (scopes.some((scope) => scope.startsWith('knowledge:'))) {
    parts.push(t('settings.integrations.keys.scopes.knowledge'))
  }

  return parts.join(', ')
}

/** Короткая дата: «12 окт». Год добавляется, только если он не текущий. */
export const shortDate = (iso: string, locale: string, now = new Date()): string => {
  if (!iso) return ''

  const date = new Date(iso)
  const sameYear = date.getFullYear() === now.getFullYear()

  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
    .format(date)
    .replace(/\s+г\.$/, '')
    .replace(/\./g, '')
}

/** Срок: у живого — до какого числа, у погашенного — когда отозван. */
export const formatExpiry = (key: ApiKey, locale: string, t: Translate, now = new Date()): string => {
  if (!key.isActive) {
    return key.revokedAt
      ? t('settings.integrations.keys.revokedAt', { date: shortDate(key.revokedAt, locale, now) })
      : t('settings.integrations.keys.expired')
  }

  return t('settings.integrations.keys.until', { date: shortDate(key.expiresAt, locale, now) })
}

/** Последнее обращение. Ключ, которым ни разу не пользовались, — тоже ответ. */
export const formatLastUsed = (
  lastUsedAt: string | undefined,
  locale: string,
  t: Translate,
  now = new Date(),
): string => {
  if (!lastUsedAt) return t('settings.integrations.keys.neverUsed')

  const used = new Date(lastUsedAt)
  const days = dayDiff(used, now)

  if (days === 0) {
    const time = new Intl.DateTimeFormat(intlLocale(locale), {
      hour: '2-digit',
      minute: '2-digit',
    }).format(used)

    return t('settings.integrations.keys.today', { time })
  }

  if (days === 1) return t('settings.integrations.keys.yesterday')

  if (days <= RELATIVE_DAYS) return t('settings.integrations.keys.daysAgo', days)

  return shortDate(lastUsedAt, locale, now)
}

/** Разница в календарных днях: «вчера в 23:50» не должно стать «сегодня». */
const dayDiff = (from: Date, to: Date): number => {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate())

  return Math.round((end.getTime() - start.getTime()) / 86_400_000)
}
